import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { FormikSaveBar } from "../../utils/FormikSaveBar";
import { PMField } from "../../utils/PMField";
import { useDispatch } from "react-redux";
import PMSwitch from "../../utils/PMSwitch";
import PMListboxFormik from "../../utils/PMListboxFormik";
import { createCustomerAsync, updateCustomerAsync } from "../../../slices/customer";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { GoogleMapsButton, dispatchWithToastPromise } from "../../Utils";
import { CustomerNumberCheck } from "./customer-number-check";
import { CustomerEmailCheck } from "./customer-email-check";

export const CustomerEditForm = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh, onLoading, onLoadingEnd, readOnly = false } = props;
  const dispatch = useDispatch();

  const isAdmin = useUserHasAccess({ role: "customer", action: "sync" });

  const customerTypes = [
    { name: "customer_type", value: "", label: "Ikke valgt" },
    { name: "customer_type", value: "private", label: "Customer" },
    { name: "customer_type", value: "company", label: "Firmakunde" },
    { name: "customer_type", value: "supplier", label: "Leverandør" },
    {
      name: "customer_type",
      value: "customer_company_supplier",
      label: "Firmakunde/ Leverandør",
    },
  ];

  return (
    <>
      <Formik
        initialValues={{
          id: item?.id,
          customer_number: item?.customer_number,
          customer_type: item?.customer_type,
          name: item?.name,
          email: item?.email,
          mobile: item?.mobile,
          vat: item?.vat,
          api_id: item?.api_id,
          postal_address: item?.postal_address?.id
            ? item?.postal_address
            : {
                address: item?.address,
                address2: item?.address_two,
                postal: item?.postal,
                place: item?.place,
              },
          physical_address: item?.physical_address?.id
            ? item?.physical_address
            : "",
          nextCustomerNumber: item?.nextCustomerNumber || false,
          submit: null,
          active: item?.active || true,
        }}
        validationSchema={Yup.object({
          name: Yup.string().max(255).required(t("customer.name.required")),
          description: Yup.string(),
        })}
        onSubmit={(values, helpers) => {
          try {
            onLoading?.();
            const action = values?.id
              ? updateCustomerAsync(values)
              : createCustomerAsync(values);
            dispatchWithToastPromise(dispatch(action), onRefresh)
              .finally(() => {
                onLoadingEnd?.();
              })
              .catch(() => {});
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            onLoadingEnd?.();
            toast.error("Nope");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <div className="shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div className="grid sm:grid-cols-8 gap-6">
                  <div className="sm:col-span-2">
                    <PMSwitch label="Active" name="active" disabled={readOnly} {...formik} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {t("Contact information")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500"></p>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="customer_number"
                      type="text"
                      label="Customer Number"
                      readOnly={readOnly}
                      {...formik}
                    />
                    <CustomerNumberCheck
                      number={formik.values.customer_number}
                      customer={item}
                    />
                  </div>

                  {isAdmin && (
                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        name="api_id"
                        type="text"
                        label="API ID"
                        disabled={readOnly || (item?.api_id ? true : false)}
                        {...formik}
                      />
                    </div>
                  )}

                  <div className="col-span-6 sm:col-span-3">
                    <PMListboxFormik
                      label={t("customer.type")}
                      name="customer_type"
                      options={customerTypes}
                      disabled={readOnly}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField name="name" type="text" label="Name" readOnly={readOnly} {...formik} />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="email"
                      type="text"
                      label="Email"
                      readOnly={readOnly}
                      {...formik}
                    />
                    <CustomerEmailCheck
                      email={formik.values.email}
                      customer={item}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <PMField
                      name="mobile"
                      type="text"
                      label="Mobile"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>

                  {formik?.values?.customer_type?.includes(
                    "company",
                    "customer_company_supplier"
                  ) && (
                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        name="vat"
                        type="text"
                        label="Org. Nummer"
                        readOnly={readOnly}
                        {...formik}
                      />
                    </div>
                  )}

                  <div className="col-span-6">
                    <PMField
                      name="short_description"
                      type="text"
                      label="Short Description"
                      multiline
                      rows={2}
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="grid grid-cols-3 sm:grid-cols-3 bg-white sm:p-6 gap-6">
                  <div className="col-span-3">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("Postal Address")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500"></p>
                      </div>
                      <div>
                        <GoogleMapsButton
                          address={formik?.values?.postal_address}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="postal_address.address"
                      type="text"
                      label="Address"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="postal_address.address2"
                      type="text"
                      label="Address 2"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <PMField
                      name="postal_address.postal"
                      type="text"
                      label="Postal"
                      placeName="postal_address.place"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <PMField
                      name="postal_address.place"
                      type="text"
                      label="Place"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-3 bg-white sm:p-6 gap-6">
                  <div className="col-span-3">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("Physical Address")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500"></p>
                      </div>
                      <div>
                        <GoogleMapsButton
                          address={formik?.values?.physical_address}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="physical_address.address"
                      type="text"
                      label="Address"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-3">
                    <PMField
                      name="physical_address.address2"
                      type="text"
                      label="Address 2"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-1">
                    <PMField
                      name="physical_address.postal"
                      type="text"
                      label="Postal"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <PMField
                      name="physical_address.place"
                      type="text"
                      label="Place"
                      readOnly={readOnly}
                      {...formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            {!readOnly && <FormikSaveBar {...formik} />}
          </Form>
        )}
      </Formik>
    </>
  );
};
